import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

interface Publication {
    title: string;
    authors: string;
    venue: string;
    year: string;
    url?: string;
    doi?: string;
}

const MinimalPublications: React.FC = () => {
    const [publications, setPublications] = useState<Publication[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchPublications = async () => {
        try {
            const API_URL = "https://api.cabhinav.com/api/server.js";
            const response = await fetch(API_URL);
            const data = await response.json();
            const articles = data.data?.articles || data.articles || [];

            if (articles.length > 0) {
                setPublications(articles.sort((a: any, b: any) => parseInt(b.year) - parseInt(a.year)));
            } else {
                throw new Error("No articles in API");
            }
        } catch (e) {
            console.error("Using fallback publications data");
            const fallbackPubs: Publication[] = [
                {
                    title: "High Power Density Solid State Marx Generator Based Pulsed Power Driver for Nonlinear Transmission Line",
                    authors: "A. Chinnusamy, H. Baik, and J. Roy",
                    venue: "2025 IEEE Energy Conversion Congress & Exposition Asia (ECCE-Asia)",
                    year: "2025",
                    doi: "10.1109/ECCE-Asia63110.2025.11112355",
                },
                {
                    title: "Implementation of Protection Features for a Modular Bidirectional Solid-State Battery Disconnect",
                    authors: "D. Dsa, A. Chinnusamy, S. N. Banavath, and E. L. Carvalho",
                    venue: "IEEE Journal of Emerging and Selected Topics in Power Electronics",
                    year: "2024",
                },
                {
                    title: "Intelligent Battery Protection System for Electric Vehicle Applications",
                    authors: "A. Chinnusamy, D. Dsa, and S. N. Banavath",
                    venue: "2024 IEEE 18th International Conference on Compatibility, Power Electronics and Power Engineering (CPE-POWERENG)",
                    year: "2024",
                },
            ];
            setPublications(fallbackPubs);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPublications();
    }, []);

    const highlightAuthor = (authors: string) => {
        return authors
            .replace(/Abhinav Chinnusamy/g, '<strong>Abhinav Chinnusamy</strong>')
            .replace(/A\. Chinnusamy/g, '<strong>A. Chinnusamy</strong>');
    }

    return (
        <div className="space-y-8">
            <Helmet>
                <title>Publications | Abhinav Chinnusamy</title>
            </Helmet>

            <header
                className="stagger-item mb-8 space-y-1"
                style={{ ['--stagger' as string]: 0 }}
            >
                <h1 className="text-sm text-muted">Publications</h1>
                <p className="text-xs text-faint">Papers and proceedings.</p>
            </header>

            {loading ? (
                <div className="text-sm text-faint">Loading…</div>
            ) : (
                <div className="space-y-6">
                    {publications.map((pub, idx) => (
                        <article
                            key={idx}
                            className="stagger-item space-y-1 border-b border-line pb-5 last:border-0"
                            style={{ ['--stagger' as string]: idx + 1 }}
                        >
                            <h3 className="text-sm text-fg">
                                {pub.url ? (
                                    <a href={pub.url} target="_blank" rel="noreferrer" className="quiet-link">
                                        {pub.title}
                                    </a>
                                ) : pub.title}
                            </h3>
                            <div className="text-xs text-muted" dangerouslySetInnerHTML={{ __html: highlightAuthor(pub.authors) }} />
                            <div className="text-xs text-faint">
                                {pub.venue} · {pub.year}
                            </div>
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MinimalPublications;
